import { useState, useEffect, useRef } from "react";
import { displayUserName, formatDisplayDate, truncateString, validateUserRole } from "../../helpers/helpers";
import { toast } from "react-toastify";
import { useDashboardUI } from "../../context/GlobalContext";

import editIcon from "../../assets/edit.svg";
import checkIcon from "../../assets/check.svg";
import trashIcon from "../../assets/trash.svg";
import xIcon from "../../assets/x.svg";
import assistantService from "../../services/assistantService";
import { userRoles } from "../../helpers/constant";
import { useNavigate } from "react-router-dom";
import { DeleteAssistantModal } from "./DeleteAssistantModal";
import { createPortal } from "react-dom";

const STATUS_CONFIG = {
  true: {
    label: "Published",
    className: "bg-[#E7F7EE] text-[#12945A]",
  },
  false: {
    label: "Unpublished",
    className: "bg-[#FDECEC] text-[#D93636]",
  },
};

const ACCESS_LEVEL = {
  "facility": "FACILITY",
  "global": "GLOBAL",
  "self": "SELF",
  "users": "USERS"
}

export function AssistantList() {
  const { selectedFacilityFilter, setPageLoader, facilityUserList } = useDashboardUI();

  const [assistantList, setAssistantList] = useState([]);

  //pagination & filters
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState("");
  const [published, setPublished] = useState(null);
  const [accessLevel, setAccessLevel] = useState(null); // "global" | "facility" | null

  //UI state
  const [openActionMenu, setOpenActionMenu] = useState(null);
  const [showDeleteAssistantConfirm, setShowDeleteAssistantConfirm] = useState(null);
  const [isDeletingAssistant, setIsDeletingAssistant] = useState(null);

  const [openFilters, setOpenFilters] = useState(false);
  const [debouncedSearch, setDebouncedSearch] = useState("");

  const buttonRef = useRef < HTMLButtonElement | null > (null);
  const [dropdownPosition, setDropdownPosition] = useState({ top: 0, left: 0 });
  // const [openActionMenu, setOpenActionMenu] = useState<number | null>(null);



  const actionMenuRef = useRef(null);
  const filterRef = useRef(null);

  const navigate = useNavigate();


  //Close ref of dropdown modal
  useEffect(() => {
    function handleClickOutside(event) {
      if (
        actionMenuRef.current &&
        !actionMenuRef.current.contains(event.target)
      ) {
        setOpenActionMenu(null); // close menu
      }

      // Close filters dropdown
      if (
        filterRef.current &&
        !filterRef.current.contains(event.target)
      ) {
        setOpenFilters(false);
      }

    }

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  // FETCH ASSISTANTS
  const fetchAssistantList = async () => {
    try {
      setPageLoader(true);
      const facility_number = selectedFacilityFilter?.facility_number;

      let query = `&page=${page}&limit=${limit}`;

      if (facility_number) query += `&facility_number=${facility_number}`;
      if (search) query += `&search=${search}`;
      if (published !== null) query += `&published=${published}`;
      if (accessLevel !== null) query += `&share_access=${accessLevel}`

      const data = await assistantService.getAssistantList(query);


      setPageLoader(false);
      setAssistantList(data.results || []);
      setTotal(data.total || 0);
      setTotalPages(data.total_pages || 1);
    } catch (error) {
      setPageLoader(false);
      console.error("Error fetching assistant list:", error);
    }
  };

  //Call if filter values change
  useEffect(() => {
    fetchAssistantList();
  }, [page, limit, published, accessLevel, debouncedSearch, selectedFacilityFilter]);

  //Debounce user typing
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
    }, 400); // debounce delay

    return () => clearTimeout(timer);
  }, [search]);


  // Delete Assistant ACTIONS
  const deleteAssistant = () => {
    setShowDeleteAssistantConfirm(assistantList[openActionMenu]);
    setOpenActionMenu(null);
  };


  //Delete Assistant
  const handleAssistantDelete = (assistantId) => async () => {
    try {
      setPageLoader(true);
      setIsDeletingAssistant(assistantId);
      await assistantService.deleteAssistant(assistantId);
      toast.success("Assistant deleted successfully");
    } catch (error) {
      toast.error("Error deleting assistant");
    } finally {
      setPageLoader(false);
      setShowDeleteAssistantConfirm(null);
      setIsDeletingAssistant(null);
      fetchAssistantList();
    }
  };


  const updateAssistant = (assistant) => {
    navigate(`/update-assistant/${assistant.id}`);
  }

  const updateAssistantStatus = (isPublished) => async () => {
    try {
      setPageLoader(true);
      const assistant = assistantList[openActionMenu];
      await assistantService.updateAssistant(assistant.id, { is_published: isPublished });
      toast.success(`Assistant ${isPublished ? "published" : "unpublished"} successfully`);
      fetchAssistantList();
    } catch (error) {
      toast.error(`Error ${isPublished ? "publishing" : "unpublishing"} assistant`);
    } finally {
      setPageLoader(false);
      setOpenActionMenu(null);
    }
  }
  // const openMenu = (e) => {
  //   e.stopPropagation();

  //   const rect = e.currentTarget.getBoundingClientRect();

  //   setDropdownPosition({
  //     top: rect.bottom + 8,
  //     left: rect.right - 224, // 224px = w-56
  //   });

  //   setOpenActionMenu(index);
  // };


  const openActionModal = (e) => {
    e.stopPropagation();

    const rect = e.currentTarget.getBoundingClientRect();
    const dropdownHeight = 240; // approx height of menu
    const spaceBelow = window.innerHeight - rect.bottom;
    const spaceAbove = rect.top;

    let top;

    if (spaceBelow < dropdownHeight && spaceAbove > dropdownHeight) {
      // open upward
      top = rect.top - dropdownHeight - 8;
    } else {
      // open downward
      top = rect.bottom + 8;
    }

    setDropdownPosition({
      top,
      left: rect.right - 224, // 224px = w-56
    });

    setOpenActionMenu(index);
  }



  // RENDER
  const isSuperAdminView = validateUserRole(userRoles.SUPERADMIN);

  return (
    <>
      <div className="w-full flex flex-col h-full lg:h-[calc(100vh-100px)] pt-4 gap-3">
        {/* Header */}
        <div className="flex flex-wrap items-center justify-between gap-3 flex-shrink-0">
          <input
            type="text"
            placeholder="Search assistant by name or description"
            value={search}
            onChange={(e) => {
              setPage(1);
              setSearch(e.target.value);
            }}
            className="w-full sm:w-80 h-10 sm:h-11 rounded-xl border border-[#ECEEF4] bg-white px-4 sm:px-5 text-sm text-gray-700 placeholder:text-[#9AA1B2] transition-colors hover:border-[#D9DCE6] focus:outline-none focus:border-[#8B5CF6]/50 focus:ring-2 focus:ring-[#8B5CF6]/10"
          />

          <div className="flex w-full flex-nowrap items-center justify-between gap-2 sm:w-auto sm:flex-wrap sm:justify-start sm:gap-3">
            {/* Status Filter */}
            <div ref={filterRef} className="flex items-center gap-3 relative">
              {/* More Filters Button */}
              <button
                onClick={() => setOpenFilters((v) => !v)}
                className="flex items-center gap-2 h-11 px-4 rounded-xl border border-[#ECEEF4] bg-white text-sm font-medium whitespace-nowrap text-[#4B5266] transition-colors hover:border-[#D9DCE6] hover:bg-gray-50"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5h18M6 12h12M10 19h4" />
                </svg>
                More filters
              </button>

              {/* Filters Dropdown */}
              {openFilters && (
                <div className="fixed inset-x-3 top-32 z-[60] w-auto max-h-[calc(100vh-9rem)] overflow-y-auto sm:absolute sm:inset-x-auto sm:top-12 sm:right-0 sm:z-30 sm:w-64 sm:max-h-none sm:overflow-visible rounded-2xl border border-gray-100 bg-white shadow-lg flex flex-col">
                  {/* Header */}
                  <div className="px-5 pt-4 pb-2 border-b border-gray-100 text-base font-bold text-slate-900 not-italic whitespace-nowrap">
                    Filters
                  </div>

                  {/* Status Section */}
                  <div className="flex flex-col px-5 pt-3 pb-1">
                    <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 not-italic whitespace-nowrap">
                      Status
                    </div>

                    <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={published === true}
                        onChange={() => {
                          setPage(1);
                          setPublished(published === true ? null : true);
                        }}
                        className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                      />
                      Published
                    </label>

                    <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                      <input
                        type="checkbox"
                        checked={published === false}
                        onChange={() => {
                          setPage(1);
                          setPublished(published === false ? null : false);
                        }}
                        className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                      />
                      Unpublished
                    </label>
                  </div>

                  {/* Access level Section */}
                  {validateUserRole(userRoles.SUPERADMIN) && (
                    <div className="flex flex-col px-5 pt-1 pb-4">
                      <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400 not-italic whitespace-nowrap">
                        Access Level
                      </div>

                      <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={accessLevel === "global"}
                          onChange={() => {
                            setPage(1);
                            setAccessLevel(accessLevel === "global" ? null : "global");
                          }}
                          className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                        />
                        Global
                      </label>

                      <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={accessLevel === "facility"}
                          onChange={() => {
                            setPage(1);
                            setAccessLevel(accessLevel === "facility" ? null : "facility");
                          }}
                          className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                        />
                        Facility
                      </label>

                      <label className="flex items-center gap-3 -mx-2 px-2 py-2 rounded-lg text-sm font-medium text-slate-700 not-italic cursor-pointer hover:bg-gray-50 whitespace-nowrap">
                        <input
                          type="checkbox"
                          checked={accessLevel === "users"}
                          onChange={() => {
                            setPage(1);
                            setAccessLevel(accessLevel === "users" ? null : "users");
                          }}
                          className="w-5 h-5 rounded-lg border-gray-300 accent-blue-500 focus:ring-blue-200 shrink-0"
                        />
                        Specific users
                      </label>
                    </div>
                  )}

                </div>
              )}
            </div>


            {/* Create Button */}
            <button
              onClick={() => navigate("/create-assistant")}
              className="flex items-center gap-2 rounded-xl bg-[#3F4BB8] px-4 sm:px-6 h-11 text-sm font-semibold whitespace-nowrap text-white hover:bg-[#363FA0] transition"
            >
              Create<span className="hidden sm:inline"> Assistant</span> <span className="text-lg">+</span>
            </button>
          </div>

        </div>

        {/* Table card (same look as Feedback Items / User Management / Support Tickets) */}
        <div className="flex-1 min-h-0 bg-white rounded-2xl overflow-hidden shadow-[0_1px_3px_rgba(20,18,43,0.04),0_8px_24px_rgba(20,18,43,0.05)]">
          <div className="table-scroll h-full overflow-auto">
          <table className="w-full min-w-[1100px] xl:min-w-[900px] table-fixed border-collapse text-sm">
            <thead>
              <tr className="border-b border-gray-100">
                {[
                  { label: "Assistant name", width: isSuperAdminView ? "19%" : "22%" },
                  { label: "Description", width: isSuperAdminView ? "27%" : "31%" },
                  { label: "Status", width: isSuperAdminView ? "11%" : "13%" },
                  ...(isSuperAdminView ? [{ label: "Access level", width: "12%" }] : []),
                  { label: "Created by", width: isSuperAdminView ? "12%" : "14%" },
                  { label: "Created at", width: isSuperAdminView ? "12%" : "14%" },
                  { label: "Action", width: isSuperAdminView ? "7%" : "6%" },
                ].map(({ label, width }) => (
                  <th
                    key={label}
                    style={{ position: "sticky", top: 0, background: "#F5F2FF", zIndex: 20, width, textAlign: "left" }}
                    className="py-3.5 pl-6 pr-3 align-middle text-xs font-semibold text-gray-500 uppercase tracking-wide"
                  >
                    {label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {assistantList && assistantList.length > 0 ? (
                assistantList.map((row, index) => (
                  <tr
                    key={row.id}
                    onClick={() => updateAssistant(row)}
                    className="cursor-pointer border-b border-gray-100 last:border-0 transition-colors hover:bg-gray-50"
                  >
                    <td className="pl-6 pr-3 py-4 align-middle text-sm font-semibold text-[#14122B]">
                      {truncateString(row.name, 50)}
                    </td>

                    <td className="pl-6 pr-3 py-4 align-middle text-gray-600">
                      {truncateString(row.description || row.content, 50)}
                    </td>

                    <td className="pl-6 pr-3 py-4 align-middle">
                      <span
                        className={`inline-flex items-center whitespace-nowrap rounded-full px-3 py-1 text-xs font-semibold ${STATUS_CONFIG[row.is_published]?.className
                          }`}
                      >
                        {STATUS_CONFIG[row.is_published]?.label}
                      </span>
                    </td>

                    {isSuperAdminView && <td className="pl-6 pr-3 py-4 align-middle text-gray-600">
                      {ACCESS_LEVEL[row?.share_access]}
                    </td>}

                    <td className="pl-6 pr-3 py-4 align-middle text-gray-600">{displayUserName(facilityUserList, row?.user_id)}</td>

                    <td className="pl-6 pr-3 py-4 align-middle text-gray-600 whitespace-nowrap">
                      {formatDisplayDate(row?.created_at)}
                    </td>

                    <td className="relative pl-6 pr-3 py-4 align-middle text-left">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();

                          const rect = e.currentTarget.getBoundingClientRect();
                          const dropdownWidth = 224;
                          const dropdownHeight = 260;

                          let top = rect.bottom + 8 + window.scrollY;
                          let left = rect.right - dropdownWidth + window.scrollX;

                          // Flip upward if not enough space below
                          if (window.innerHeight - rect.bottom < dropdownHeight) {
                            top = rect.top - dropdownHeight - 8 + window.scrollY;
                          }

                          // Prevent right overflow
                          if (left + dropdownWidth > window.innerWidth + window.scrollX) {
                            left = window.innerWidth - dropdownWidth - 8 + window.scrollX;
                          }

                          // Prevent left overflow
                          if (left < 8 + window.scrollX) {
                            left = 8 + window.scrollX;
                          }

                          setDropdownPosition({ top, left });
                          setOpenActionMenu(index);
                        }}


                        className="rounded-full p-2 hover:bg-gray-100"
                      >
                        <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                          <path d="M12 13C12.5523 13 13 12.5523 13 12C13 11.4477 12.5523 11 12 11C11.4477 11 11 11.4477 11 12C11 12.5523 11.4477 13 12 13Z" stroke="#181D27" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 6C12.5523 6 13 5.55228 13 5C13 4.44772 12.5523 4 12 4C11.4477 4 11 4.44772 11 5C11 5.55228 11.4477 6 12 6Z" stroke="#181D27" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                          <path d="M12 20C12.5523 20 13 19.5523 13 19C13 18.4477 12.5523 18 12 18C11.4477 18 11 18.4477 11 19C11 19.5523 11.4477 20 12 20Z" stroke="#181D27" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      </button>

                      {openActionMenu === index && dropdownPosition && 
                        createPortal(

                          <div ref={actionMenuRef} className="fixed z-[9999] w-56 rounded-xl border bg-white shadow-lg"
                            style={{
                              top: dropdownPosition.top,
                              left: dropdownPosition.left,
                            }}
                          >
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateAssistantStatus(true)();
                              }}
                              disabled={row.is_published}
                              className="flex w-full items-center gap-3 px-4 py-3 text-sm text-green-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              <img src={checkIcon} alt="" /> Publish
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateAssistantStatus(false)();
                              }}
                              disabled={!row.is_published}
                              className="flex w-full items-center gap-3 px-4 py-3 text-sm text-red-700 hover:bg-gray-50"
                            >
                              <img src={xIcon} alt="" /> Unpublish
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                deleteAssistant();
                              }}
                              className="flex w-full items-center gap-3 px-4 py-3 text-sm text-red-700 hover:bg-gray-50"
                            >
                              <img src={trashIcon} alt="" /> Delete
                            </button>

                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                updateAssistant(row);
                              }}
                              className="flex w-full items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50"
                            >
                              <img src={editIcon} alt="" /> Update Assistant
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/update-assistant-files/${row.id}`);
                              }}
                              className="flex w-full items-center gap-3 px-4 py-3 text-sm hover:bg-gray-50"
                            >
                              <img src={editIcon} alt="" /> Update Assistant Files
                            </button>
                          </div>,
                          document.body
                        )}
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td
                    colSpan={6}
                    className="px-6 py-10 text-center text-sm text-gray-500"
                  >
                    No assistant found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* Pagination (same layout as Feedback Items / User Management) */}
        <div className="mt-auto pt-1 pb-1 flex flex-nowrap items-center justify-between gap-2 whitespace-nowrap text-sm flex-shrink-0">
          <span>Total {total}</span>

          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              Rows per page
              <select
                value={limit}
                onChange={(e) => {
                  setPage(1);
                  setLimit(Number(e.target.value));
                }}
                className="rounded-md border px-2 py-1"
              >
                <option value={10}>10</option>
                <option value={20}>20</option>
                <option value={30}>30</option>
              </select>
            </div>

            <div className="flex items-center gap-2">
              <button
                disabled={page === 1}
                onClick={() => setPage(page - 1)}
                className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl"
              >
                ‹
              </button>

              <span>
                {page} / {totalPages}
              </span>

              <button
                disabled={page === totalPages}
                onClick={() => setPage(page + 1)}
                className="px-1 text-base leading-none sm:px-2.5 sm:py-0.5 sm:text-2xl"
              >
                ›
              </button>
            </div>
          </div>
        </div>

      </div>

      <DeleteAssistantModal
        open={!!showDeleteAssistantConfirm}
        onClose={() => setShowDeleteAssistantConfirm(null)}
        onDelete={handleAssistantDelete(showDeleteAssistantConfirm?.id)}
        assistant={showDeleteAssistantConfirm}
        isDeleting={isDeletingAssistant === showDeleteAssistantConfirm?.id}
      />
    </>
  );
}
